import React, { useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Typography,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import api from '../../../../config/axiosConfig';
import { API } from '../../../../utils/apiRoutes';
import { formatJalaliDate } from '../../../../utils/formatJalaliDate';
import { toPersianDigits } from '../../../../utils/convertNumbers';

// Spec 006 §4.6: the model only proposes; this card is the only place an
// assistant write is executed, and only after an explicit click.

const STATUS_META = {
    pending: { label: 'در انتظار تأیید', color: 'primary' },
    confirmed: { label: 'در حال ثبت', color: 'info' },
    executed: { label: 'ثبت شد', color: 'success' },
    failed: { label: 'ناموفق', color: 'error' },
    cancelled: { label: 'لغو شد', color: 'default' },
    expired: { label: 'منقضی شد', color: 'default' },
};

function formatCountdown(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const minutes = String(Math.floor(total / 60)).padStart(2, '0');
    const seconds = String(total % 60).padStart(2, '0');
    return toPersianDigits(`${minutes}:${seconds}`);
}

function extractDetail(err, fallback) {
    return err?.response?.data?.detail || fallback;
}

function ItemRow({ item, checkable, checked, onToggle, created, error, notSelected }) {
    const after = item.remained_after;
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.5,
                py: 0.75,
                opacity: notSelected ? 0.55 : 1,
                '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' },
            }}
        >
            {checkable && (
                <Checkbox
                    size="small"
                    checked={checked}
                    onChange={onToggle}
                    inputProps={{ 'aria-label': `انتخاب ${item.student || ''}` }}
                    // 24px tall, like the name/chip line it sits beside.
                    sx={{ p: 0.25 }}
                />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.student || '—'}
                    </Typography>
                    <Chip size="small" color="primary" variant="outlined" label={item.status} />
                </Box>
                <Typography variant="caption" color="text.secondary" component="div">
                    {[item.course, item.teacher].filter(Boolean).join(' · ') || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                    {toPersianDigits(
                        `${item.weekday ? `${item.weekday} ` : ''}${(item.date || '').replace(/-/g, '/')}`,
                    )}
                    {' · جلسات باقی‌مانده: '}
                    <Box component="span" sx={{ direction: 'ltr', display: 'inline-block' }}>
                        {toPersianDigits(`${item.remained_before ?? '—'} → `)}
                        <Box
                            component="span"
                            sx={{ color: after < 0 ? 'error.main' : 'inherit', fontWeight: 700 }}
                        >
                            {toPersianDigits(after ?? '—')}
                        </Box>
                    </Box>
                </Typography>
                {item.comment && (
                    <Typography variant="caption" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                        {toPersianDigits(item.comment)}
                    </Typography>
                )}
                {(item.warnings || []).length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {item.warnings.map((warning) => (
                            <Chip
                                key={warning}
                                size="small"
                                color="warning"
                                variant="outlined"
                                icon={<WarningAmberOutlinedIcon />}
                                label={warning}
                                sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.65rem' } }}
                            />
                        ))}
                    </Box>
                )}
                {created && (
                    <Typography variant="caption" color="success.main" component="div" sx={{ mt: 0.5 }}>
                        {toPersianDigits(
                            `ثبت شد (ردیف ${created.operation_id}) · ${formatJalaliDate(created.date_belonged)}` +
                                (created.remained_sessions != null
                                    ? ` · باقی‌مانده: ${created.remained_sessions}`
                                    : ''),
                        )}
                    </Typography>
                )}
                {error && (
                    <Typography variant="caption" color="error.main" component="div" sx={{ mt: 0.5 }}>
                        {toPersianDigits(error)}
                    </Typography>
                )}
                {notSelected && (
                    <Typography variant="caption" color="text.secondary" component="div">
                        انتخاب نشده بود
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function AssistantActionCard({ action, serverOffsetMs = 0, canAct, onChanged, onError }) {
    const items = useMemo(() => action.preview?.items || [], [action.preview]);
    const [checked, setChecked] = useState(() => items.map((item) => item.index));
    const [busy, setBusy] = useState(false);
    const [now, setNow] = useState(() => Date.now() + serverOffsetMs);

    const expiresAt = action.expires_at ? Date.parse(action.expires_at) : null;
    const remainingMs = expiresAt ? expiresAt - now : 0;
    // The server flips expiry lazily; mirror it locally so a stale card can't be confirmed.
    const status = action.status === 'pending' && expiresAt && remainingMs <= 0 ? 'expired' : action.status;
    const isPending = status === 'pending';
    const meta = STATUS_META[status] || { label: status, color: 'default' };

    useEffect(() => {
        if (!isPending) return undefined;
        const timer = setInterval(() => setNow(Date.now() + serverOffsetMs), 1000);
        return () => clearInterval(timer);
    }, [isPending, serverOffsetMs]);

    const result = action.result || {};
    const createdByIndex = useMemo(
        () => Object.fromEntries((result.created || []).map((row) => [row.index, row])),
        [result.created],
    );
    const errorByIndex = useMemo(
        () => Object.fromEntries((result.errors || []).map((row) => [row.index, row.detail])),
        [result.errors],
    );
    const selectedSet = Array.isArray(result.selected) ? new Set(result.selected) : null;

    const toggle = (index) =>
        setChecked((prev) =>
            prev.includes(index) ? prev.filter((value) => value !== index) : [...prev, index],
        );

    const post = async (path, body, fallback) => {
        setBusy(true);
        try {
            await api.post(`${API.assistantActions}${action.id}/${path}/`, body);
        } catch (err) {
            onError?.(extractDetail(err, fallback));
        } finally {
            setBusy(false);
            // 400/409 also return the updated action; reload the thread either way.
            onChanged?.();
        }
    };

    const confirm = () =>
        post(
            'confirm',
            { items: [...checked].sort((a, b) => a - b) },
            'ثبت حضور و غیاب انجام نشد.',
        );
    const cancel = () => post('cancel', undefined, 'انصراف انجام نشد.');

    return (
        <Box
            sx={(theme) => ({
                mt: 1,
                p: 1.25,
                borderRadius: 2,
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            })}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                <FactCheckOutlinedIcon fontSize="small" color="primary" />
                <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
                    {toPersianDigits(`ثبت حضور و غیاب (${items.length} مورد)`)}
                </Typography>
                {isPending && expiresAt && (
                    <Typography variant="caption" color="text.secondary">
                        {`اعتبار: ${formatCountdown(remainingMs)}`}
                    </Typography>
                )}
                <Chip size="small" color={meta.color} label={meta.label} />
            </Box>

            <Box>
                {items.map((item) => (
                    <ItemRow
                        key={item.index}
                        item={item}
                        checkable={isPending && canAct}
                        checked={checked.includes(item.index)}
                        onToggle={() => toggle(item.index)}
                        created={createdByIndex[item.index]}
                        error={errorByIndex[item.index]}
                        notSelected={
                            status === 'executed' && selectedSet && !selectedSet.has(item.index)
                        }
                    />
                ))}
            </Box>

            {status === 'expired' && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    زمان تأیید گذشته است؛ در صورت نیاز دوباره از دستیار بخواهید.
                </Typography>
            )}

            {isPending && canAct && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                    <Button size="small" onClick={cancel} disabled={busy}>
                        انصراف
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={confirm}
                        disabled={busy || checked.length === 0}
                    >
                        {busy ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : (
                            toPersianDigits(`تأیید (${checked.length})`)
                        )}
                    </Button>
                </Box>
            )}
        </Box>
    );
}

export default AssistantActionCard;
